import { motion, AnimatePresence } from 'framer-motion';
import { useI18n } from '@/lib/i18n';
import { useState } from 'react';
import { Plus, Minus } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

const fallbackSteps = [
  { num: '01', titleRu: 'Бриф', titleEn: 'Brief', descRu: '', descEn: '' },
  { num: '02', titleRu: 'Ресерч', titleEn: 'Research', descRu: '', descEn: '' },
  { num: '03', titleRu: 'Концептинг', titleEn: 'Concepting', descRu: '', descEn: '' },
  { num: '04', titleRu: 'Стиллфреймы', titleEn: 'Stillframes', descRu: '', descEn: '' },
  { num: '05', titleRu: 'Продакшн', titleEn: 'Production', descRu: '', descEn: '' },
  { num: '06', titleRu: 'Контроль', titleEn: 'Control', descRu: '', descEn: '' },
];

const Process = () => {
  const { lang, t } = useI18n();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const { data: settings } = useQuery({
    queryKey: ['site-settings'],
    queryFn: async () => {
      const { data } = await supabase.from('site_settings').select('*').maybeSingle();
      return data;
    },
  });

  const { data: dbSteps } = useQuery({
    queryKey: ['process-steps'],
    queryFn: async () => {
      const { data } = await supabase.from('process_steps').select('*').order('sort_order');
      return data || [];
    },
  });

  const sectionTitle = lang === 'ru'
    ? (settings?.process_title_ru) || 'Процесс'
    : (settings?.process_title_en) || 'How I Work';

  const steps = dbSteps && dbSteps.length > 0
    ? dbSteps.map(s => ({
        num: s.number, titleRu: s.title_ru, titleEn: s.title_en,
        descRu: s.description_ru, descEn: s.description_en,
      }))
    : fallbackSteps;

  const toggle = (i: number) => {
    setOpenIndex(openIndex === i ? null : i);
  };

  return (
    <section id="process" className="py-24 md:py-32">
      <div className="container">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-4xl md:text-6xl lg:text-7xl font-display font-bold mb-16 text-center uppercase"
        >
          {sectionTitle}
        </motion.h2>

        <div className="max-w-3xl mx-auto">
          {steps.map((step, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="border-b border-border"
            >
              <button
                onClick={() => toggle(i)}
                className="w-full flex items-center justify-between py-6 md:py-8 text-left group"
              >
                <div className="flex items-center gap-4 md:gap-6">
                  <span className="text-primary text-sm font-display font-medium tracking-wider">
                    {step.num}
                  </span>
                  <span className="text-lg md:text-2xl font-display font-bold uppercase tracking-wide group-hover:text-primary transition-colors duration-300">
                    {lang === 'ru' ? step.titleRu : step.titleEn}
                  </span>
                </div>
                <span className="text-primary ml-4 flex-shrink-0">
                  {openIndex === i ? <Minus size={20} /> : <Plus size={20} />}
                </span>
              </button>
              <AnimatePresence>
                {openIndex === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.4, ease: 'easeOut' }}
                    className="overflow-hidden"
                  >
                    <div className="pb-6 md:pb-8 px-10 md:px-16 text-muted-foreground text-sm md:text-base leading-relaxed space-y-2">
                      {(lang === 'ru' ? step.descRu : step.descEn).split('\n').map((p, j) => (
                        <p key={j}>{p}</p>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Process;
